import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import {
  useLeaveDelegate,
  useLeavePeople,
  useUpsertLeaveDelegate,
} from '@/products/leave/features/use-leave';

type DelegateDraft = {
  delegateUserId: string | null;
  validFrom: string;
  validTo: string;
};

export function LeaveDelegatePage() {
  usePageTitle('Genehmigungsvertretung');
  const copy = useVoiceCopy();
  const delegate = useLeaveDelegate();
  const save = useUpsertLeaveDelegate();
  const [query, setQuery] = useState('');
  const people = useLeavePeople(query);
  const [draft, setDraft] = useState<DelegateDraft | null>(null);

  const values: DelegateDraft = draft ?? {
    delegateUserId: delegate.data?.delegateUserId ?? null,
    validFrom: delegate.data?.validFrom ?? '',
    validTo: delegate.data?.validTo ?? '',
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await save.mutateAsync(values);
      setDraft(null);
      toast.success('Genehmigungsvertretung gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vertretung konnte nicht gespeichert werden.',
      );
    }
  }

  async function onClear() {
    try {
      await save.mutateAsync({
        delegateUserId: null,
        validFrom: values.validFrom || new Date().toISOString().slice(0, 10),
        validTo: values.validTo || new Date().toISOString().slice(0, 10),
      });
      setDraft(null);
      toast.success('Genehmigungsvertretung entfernt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vertretung konnte nicht entfernt werden.',
      );
    }
  }

  function patch(valuesPatch: Partial<DelegateDraft>) {
    setDraft((current) => ({ ...(current ?? values), ...valuesPatch }));
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Genehmigungsvertretung"
        description={copy('leave.delegate.description')}
      />
      {delegate.isPending ? (
        <p className="text-sm text-muted-foreground">Einstellungen werden geladen …</p>
      ) : null}
      {delegate.isError ? (
        <p className="text-sm text-destructive">
          {delegate.error instanceof ApiRequestError
            ? delegate.error.message
            : 'Die Vertretung konnte nicht geladen werden.'}
        </p>
      ) : (
        <form
          className={`${leavePanelClassName} space-y-4`}
          onSubmit={(event) => void onSubmit(event)}
        >
          <div>
            <Label htmlFor="delegate-search">Person</Label>
            <Input
              id="delegate-search"
              className="mt-2"
              value={query}
              placeholder="Name eingeben …"
              onChange={(event) => setQuery(event.target.value)}
            />
            {people.data?.items.length ? (
              <ul className="mt-2 space-y-1 rounded-md border p-2">
                {people.data.items.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      className={`w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${
                        values.delegateUserId === person.id ? 'bg-muted font-medium' : ''
                      }`}
                      onClick={() => patch({ delegateUserId: person.id })}
                    >
                      {person.displayName}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {delegate.data?.delegate ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Aktuell: {delegate.data.delegate.displayName}
              </p>
            ) : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="valid-from">Gültig ab</Label>
              <Input
                id="valid-from"
                type="date"
                className="mt-2"
                value={values.validFrom}
                required
                onChange={(event) => patch({ validFrom: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="valid-to">Gültig bis</Label>
              <Input
                id="valid-to"
                type="date"
                className="mt-2"
                value={values.validTo}
                required
                onChange={(event) => patch({ validTo: event.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={save.isPending}>
              Speichern
            </Button>
            {delegate.data?.delegateUserId ? (
              <Button
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={() => void onClear()}
              >
                Entfernen
              </Button>
            ) : null}
          </div>
        </form>
      )}
    </div>
  );
}
